import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { config } from '../../config/runtime';

// ── Journal cost model ───────────────────────────────────────────────────────

export interface JournalEntry {
  id: string;
  agent?: string;
  event: string;
  timestamp: string;
  goal_id?: string;
  item_id?: string;
}

export interface AgentCostData {
  agentId: string;
  agentName: string;
  inputTokens: number;
  outputTokens: number;
  estimatedCost?: number;
}

const TOKEN_ESTIMATES: Record<string, number> = {
  item_created: 300,
  item_completed: 400,
  item_blocked: 200,
  comment_added: 500,
  goal_created: 200,
  agent_heartbeat: 100,
};

function estimateTokens(entry: JournalEntry): { input: number; output: number } {
  const base = TOKEN_ESTIMATES[entry.event] ?? 250;
  return { input: Math.round(base * 0.7), output: Math.round(base * 0.3) };
}

function estimateAgentCost(agentId: string, inputTokens: number, outputTokens: number): number {
  const id = agentId.toLowerCase();
  if (id === 'codex') {
    return ((inputTokens + outputTokens) / 1000) * 0.003;
  }
  if (id === 'opencode') return 0;
  // Claude Sonnet: $3/1M input + $15/1M output
  return (inputTokens / 1_000_000) * 3 + (outputTokens / 1_000_000) * 15;
}

export function useJournalCosts(): {
  costs: AgentCostData[];
  loading: boolean;
  error: string | null;
} {
  const [costs, setCosts] = useState<AgentCostData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${config.apiUrl}/api/projects/journal`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: { items?: JournalEntry[] }) => {
        const entries = data.items ?? [];
        const byAgent: Record<string, AgentCostData> = {};
        for (const entry of entries) {
          const agentId = entry.agent ?? 'unknown';
          if (!byAgent[agentId]) {
            byAgent[agentId] = {
              agentId,
              agentName: agentId.charAt(0).toUpperCase() + agentId.slice(1),
              inputTokens: 0,
              outputTokens: 0,
              estimatedCost: 0,
            };
          }
          const { input, output } = estimateTokens(entry);
          byAgent[agentId].inputTokens += input;
          byAgent[agentId].outputTokens += output;
          (byAgent[agentId].estimatedCost as number) += estimateAgentCost(agentId, input, output);
        }
        setCosts(Object.values(byAgent));
        setLoading(false);
      })
      .catch(err => {
        setError((err as Error).message ?? 'Failed to load journal');
        setLoading(false);
      });
  }, []);

  return { costs, loading, error };
}

// ── Agent list ───────────────────────────────────────────────────────────────

export interface AgentItem {
  id: string;
  name: string;
  status: 'online' | 'offline' | 'busy';
}

export function useAgentsList(): { agents: AgentItem[]; loading: boolean } {
  const [agents, setAgents] = useState<AgentItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${config.apiUrl}/api/agents`)
      .then(r => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then((data: { agents?: AgentItem[] }) => {
        setAgents(data.agents ?? []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return { agents, loading };
}

interface SSEConfig {
  url: string;
  onMessage?: (data: any) => void;
  onError?: (error: Event) => void;
  reconnectInterval?: number;
}

export function useSSE<T>(config: SSEConfig) {
  const { url, onMessage, onError, reconnectInterval = 5000 } = config;
  const eventSourceRef = useRef<EventSource | null>(null);
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const connect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const eventSource = new EventSource(url);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setIsConnected(true);
      setError(null);
    };

    eventSource.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);
        setData(parsed);
        onMessage?.(parsed);
      } catch (err) {
        console.error('Failed to parse SSE message:', err);
      }
    };

    eventSource.onerror = (err) => {
      setIsConnected(false);
      setError('Connection lost');
      onError?.(err);

      eventSource.close();
      
      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, reconnectInterval);
    };
  }, [url, onMessage, onError, reconnectInterval]);

  useEffect(() => {
    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connect]);

  return { data, error, isConnected, reconnect: connect };
}

export function useAgentUpdates() {
  return useSSE(`${config.apiUrl}/api/projects/agents/stream`);
}

export function useGoalUpdates() {
  return useSSE(`${config.apiUrl}/api/projects/goals/stream`);
}

export function useItemUpdates(goalId?: string) {
  const url = goalId
    ? `${config.apiUrl}/api/projects/goals/${goalId}/items/stream`
    : `${config.apiUrl}/api/projects/items/stream`;
  return useSSE(url);
}

// ── Journal stream ────────────────────────────────────────────────────────────

/**
 * A journal entry as returned by the stream (matches the DB shape exactly).
 * The `ts` field is an ISO-8601 string; `payload` is included when the
 * full row is pushed (history events) but omitted in the lightweight NOTIFY
 * envelope (insert events — consumers should fetch full payload if needed).
 */
export interface LiveJournalEntry {
  id: number;
  goalId?: string | null;
  itemId?: string | null;
  agent: string;
  event: string;
  ts: string;
  payload?: Record<string, unknown> | null;
}

export interface JournalStreamFilters {
  goalId?: string;
  agent?: string;
  eventType?: string;
}

export interface UseJournalStreamOptions extends JournalStreamFilters {
  /** Maximum entries to keep in memory (ring-buffer, oldest dropped). Default: 200 */
  maxEntries?: number;
  /** Number of recent entries to request as initial history. Default: 20 */
  historyCount?: number;
  /** Reconnect interval in ms. Default: 5000 */
  reconnectInterval?: number;
}

export interface UseJournalStreamResult {
  entries: LiveJournalEntry[];
  isConnected: boolean;
  isPaused: boolean;
  error: string | null;
  /** Pause live accumulation (events are buffered). */
  pause: () => void;
  /** Resume — flushes the buffered events into the visible list. */
  resume: () => void;
  /** Clear the entry list (does not disconnect). */
  clear: () => void;
}

const MAX_ENTRIES_DEFAULT = 200;
const MAX_PENDING_BUFFER = 500;

/**
 * Connects to GET /api/projects/journal/stream (SSE) and accumulates live
 * journal events.
 *
 * Pause/resume semantics: while paused new events queue in a pending buffer
 * instead of the visible entries list. On resume the buffer is flushed (with
 * the ring-buffer cap applied across the combined set).
 */
export function useJournalStream(options: UseJournalStreamOptions = {}): UseJournalStreamResult {
  const {
    goalId,
    agent,
    eventType,
    maxEntries = MAX_ENTRIES_DEFAULT,
    historyCount = 20,
    reconnectInterval = 5000,
  } = options;

  const [entries, setEntries] = useState<LiveJournalEntry[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPausedRef = useRef(false);
  const pendingRef = useRef<LiveJournalEntry[]>([]);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const url = useMemo(() => {
    const params = new URLSearchParams();
    if (goalId) params.set('goalId', goalId);
    if (agent) params.set('agent', agent);
    if (eventType) params.set('eventType', eventType);
    params.set('history', String(historyCount));
    return `${config.apiUrl}/api/projects/journal/stream?${params.toString()}`;
  }, [goalId, agent, eventType, historyCount]);

  const appendEntry = useCallback((entry: LiveJournalEntry) => {
    if (isPausedRef.current) {
      pendingRef.current = pendingRef.current.slice(-(MAX_PENDING_BUFFER - 1)).concat(entry);
      return;
    }
    setEntries(prev => prev.concat(entry).slice(-maxEntries));
  }, [maxEntries]);

  const connect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onopen = () => {
      setIsConnected(true);
      setError(null);
    };

    // History batch (one event per entry, emitted oldest-first on connect)
    es.addEventListener('journal.history', (ev: MessageEvent) => {
      try {
        const entry = JSON.parse(ev.data) as LiveJournalEntry;
        appendEntry(entry);
      } catch {
        // Ignore malformed
      }
    });

    // Live insert events
    es.addEventListener('journal.insert', (ev: MessageEvent) => {
      try {
        // The insert event data is: { type: "journal.insert", data: { id, goal_id, ... } }
        const outer = JSON.parse(ev.data) as { type: string; data: LiveJournalEntry };
        const raw = outer.data ?? (outer as unknown as LiveJournalEntry);
        // Normalise snake_case NOTIFY envelope → camelCase shape
        const entry: LiveJournalEntry = {
          id: raw.id,
          goalId: (raw as unknown as Record<string, unknown>)['goal_id'] as string | undefined ?? raw.goalId,
          itemId: (raw as unknown as Record<string, unknown>)['item_id'] as string | undefined ?? raw.itemId,
          agent: raw.agent,
          event: raw.event,
          ts: raw.ts,
          payload: raw.payload,
        };
        appendEntry(entry);
      } catch {
        // Ignore malformed
      }
    });

    es.onerror = () => {
      setIsConnected(false);
      setError('Connection lost — reconnecting…');
      es.close();
      reconnectTimerRef.current = setTimeout(connect, reconnectInterval);
    };
  }, [url, appendEntry, reconnectInterval]);

  useEffect(() => {
    connect();
    return () => {
      eventSourceRef.current?.close();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, [connect]);

  const pause = useCallback(() => {
    isPausedRef.current = true;
    setIsPaused(true);
  }, []);

  const resume = useCallback(() => {
    isPausedRef.current = false;
    setIsPaused(false);
    const pending = pendingRef.current;
    pendingRef.current = [];
    if (pending.length > 0) {
      setEntries(prev => prev.concat(pending).slice(-maxEntries));
    }
  }, [maxEntries]);

  const clear = useCallback(() => {
    setEntries([]);
    pendingRef.current = [];
  }, []);

  return { entries, isConnected, isPaused, error, pause, resume, clear };
}
